/**
 * Generate a map analysis/report prompt.
 * Works for both problem-solving maps and knowledge/concept maps.
 * @param {string} mapJson
 * @returns {string}
 */
export const generateAnalyzeMapPrompt = (mapJson) => `
Sei un analista esperto. Ricevi la struttura JSON di una mappa mentale e produci un report che aiuti l'utente a capire meglio cosa ha costruito.

MAPPA:
${mapJson}

Istruzioni:
- Ignora nodi con "banned": true
- Adatta il tono al contenuto: se è una mappa di problem-solving parla di soluzioni e priorità; se è una mappa conoscitiva parla di concetti e relazioni; se è mista, gestisci entrambi
- "highlights": i 2-4 nodi o insight più rilevanti — quelli che il lettore deve assolutamente capire/ricordare. Non elencarli tutti, solo quelli che contano davvero
- "actions": 3-5 prossimi passi concreti e attuabili. Ogni action deve essere breve, chiara e trasformabile in un nodo. Titolo in forma di concetto (no verbi all'infinito, niente "integrare/aggiungere/creare")
- "connections": connessioni non ovvie tra concetti della mappa che non emergono esplicitamente dai link. Se tutto è già ovvio, scrivi null
- "gaps": cosa manca, cosa non è sviluppato abbastanza, angolature assenti. Se la mappa è completa, scrivi null
- "takeaway": una frase sola, la cosa più importante che emerge da questa mappa
- Tono: diretto, intelligente, nerd. Zero filler, zero burocratico

Output SOLO JSON valido:
\`\`\`json
{
  "synthesis": "2-4 frasi che raccontano cosa rappresenta questa mappa e il suo punto centrale. Cita nodi per nome quando pertinente.",
  "highlights": [
    {
      "title": "Titolo del nodo o nome dell'insight",
      "why": "Perché è rilevante — 1-2 frasi dirette",
      "nodeId": "Se è un nodo specifico, usa il suo id breve (es: n3). Altrimenti null"
    }
  ],
  "actions": [
    {
      "title": "Azione concreta e breve",
      "why": "Perché è utile / impatto atteso",
      "nodeId": "Se l'azione si collega chiaramente a un nodo, usa il suo id breve. Altrimenti null",
      "priority": "high | medium | low"
    }
  ],
  "connections": "1-2 frasi su relazioni non ovvie tra concetti della mappa, oppure null",
  "gaps": "Cosa manca o va approfondito, oppure null",
  "takeaway": "La singola cosa più importante che emerge — una frase"
}
\`\`\`

LINGUA: rispondi sempre nella stessa lingua con cui scrive l'utente. Se non riesci a determinare la lingua, usa l'italiano come fallback.`;

/**
 * Parse AI report JSON response.
 * @param {string} text
 * @returns {Object|null}
 */
export const parseReportResponse = (text) => {
  try {
    const match = text.match(/```(?:json)?\s*([\s\S]*?)```/);
    const json = match ? match[1] : text;
    return JSON.parse(json.trim());
  } catch {
    return null;
  }
};
